import React, { useState } from 'react'
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField as MuiTextField,
} from '@mui/material'
import { StadsprognoseFormData, StuurkaartRow } from '../../../types/stadsprognoseTypes'
import { postJson, getUrl } from '../../../providers/data'

interface Props {
  formData: StadsprognoseFormData
  onChange: (updates: Partial<StadsprognoseFormData>) => void
}

const STUURKAART_COLUMNS: { key: keyof StuurkaartRow; label: string }[] = [
  { key: 'jaar', label: 'jaar' },
  { key: 'tfrn', label: 'tfrn' },
  { key: 'emic', label: 'emic' },
  { key: 'emuc', label: 'emuc' },
  { key: 'salit', label: 'salit' },
  { key: 'salut', label: 'salut' },
  { key: 'finwh', label: 'finwh' },
  { key: 'sexratioM', label: 'sexratioM' },
  { key: 'stmc', label: 'stmc' },
  { key: 'stvc', label: 'stvc' },
  { key: 'fbewwon', label: 'fbewwon' },
]

const Step5Stuurkaart: React.FC<Props> = ({ formData, onChange }) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const generateStuurkaart = async () => {
    if (!formData.gemeente_code) {
      setError('Geen gemeente geselecteerd')
      return
    }

    setLoading(true)
    setError(null)

    try {
      const result = await postJson<StuurkaartRow[]>(
        `${getUrl()}/gemeenten/${formData.gemeente_code}/stadsprognose/stuurkaart`,
        {
          bjaar: formData.bjaar,
          ejaar: formData.ejaar,
          bron: formData.bron,
          calc_method_salit: formData.calc_method_salit,
          calc_method_stk: formData.calc_method_stk,
          aanname_tfrn: formData.aanname_tfrn,
          aanname_geb: formData.aanname_geb,
          aanname_ste: formData.aanname_ste,
          aanname_ver: formData.aanname_ver,
          aanname_ves: formData.aanname_ves,
          correctie_sexratio: formData.correctie_sexratio,
          correctie_stk: formData.correctie_stk,
          correctie_tfrn: formData.correctie_tfrn,
          correctie_salut: formData.correctie_salut,
          correctie_fbewwon: formData.correctie_fbewwon,
          selected_wbp_code: formData.selected_wbp_code,
          selected_ext_code: formData.selected_ext_code,
          selected_matrix_code: formData.selected_matrix_code,
        }
      )
      onChange({ stuurkaart_data: result })
    } catch (err) {
      setError(`Fout bij genereren stuurkaart: ${String(err)}`)
    } finally {
      setLoading(false)
    }
  }

  const handleCellEdit = (rowIndex: number, key: keyof StuurkaartRow, value: string) => {
    if (!formData.stuurkaart_data) return
    const newData = [...formData.stuurkaart_data]
    newData[rowIndex] = {
      ...newData[rowIndex],
      [key]: key === 'jaar' ? parseInt(value, 10) : parseFloat(value),
    }
    onChange({ stuurkaart_data: newData })
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Gegenereerde Stuurkaart</Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Alle velden (m.u.v. het jaar) zijn te bewerken.
      </Typography>

      <Box sx={{ mb: 2 }}>
        <Button
          variant="contained"
          onClick={() => void generateStuurkaart()}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : undefined}
        >
          {formData.stuurkaart_data ? 'Stuurkaart opnieuw genereren' : 'Stuurkaart genereren'}
        </Button>
      </Box>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>
      )}

      {formData.stuurkaart_data && formData.stuurkaart_data.length > 0 && (
        <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {STUURKAART_COLUMNS.map((col) => (
                  <TableCell key={col.key} sx={{ fontWeight: 'bold', minWidth: col.key === 'jaar' ? 70 : 90 }}>
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {formData.stuurkaart_data.map((row, rowIndex) => (
                <TableRow key={row.jaar}>
                  {STUURKAART_COLUMNS.map((col) => (
                    <TableCell key={col.key} sx={{ p: 0.5 }}>
                      {col.key === 'jaar' ? (
                        <Typography variant="body2">{row.jaar}</Typography>
                      ) : (
                        <MuiTextField
                          size="small"
                          type="number"
                          value={row[col.key]}
                          onChange={(e) => handleCellEdit(rowIndex, col.key, e.target.value)}
                          inputProps={{
                            step: 0.001,
                            style: { fontSize: '0.8rem', padding: '4px 8px' },
                          }}
                          sx={{ width: '100%' }}
                        />
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!formData.stuurkaart_data && !loading && (
        <Typography variant="body2" color="textSecondary">
          Klik op &quot;Stuurkaart genereren&quot; om de correctiefactoren te berekenen.
        </Typography>
      )}
    </Box>
  )
}

export default Step5Stuurkaart
